import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';
import { FaKey, FaCheck, FaTimes } from 'react-icons/fa';
import { TiDocumentAdd } from 'react-icons/ti';
import { toast } from 'react-toastify';
import useUserStore from '../../store/user-store';
import useOrganisationStore from '../../store/organisations-store';
import usePaginatedListPage from '../../shared/hooks/usePaginatedListPage';
import useSearchableFilter from '../../shared/hooks/useSearchableFilter';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import ClearFiltersButton from '../../shared/components/table/ClearFiltersButton';
import ListFooter from '../../shared/components/table/ListFooter';
import RowActions from '../../shared/components/table/RowActions';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import ResetPasswordModal from '../../shared/components/modal/ResetPasswordModal';
import UploadTemplateDoc from '../../shared/components/UploadTemplateDoc';
import { formatDate } from '../../shared/lib/formatters';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';

const columnHelper = createColumnHelper();

const UsersPage = () => {
  const { users, totalUsers, totalPages, fetchUsers, createUser, editUser, deleteUser, setUserPassword } = useUserStore();
  const { searchOrganisations } = useOrganisationStore();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [templateDocUpdate, setAddTemplateDoc] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterAdmins, setFilterAdmins] = useState(false);

  const {
    value: selectedOrg,
    label: selectedOrgLabel,
    onChange: setOrgSelection,
    clear: clearOrgFilter,
    loadOptions: loadOrgOptions,
  } = useSearchableFilter(searchOrganisations);

  const fetchData = useCallback(
    async (page, search, orgId, filterAdminsArg) => {
      await fetchUsers(page, search, orgId, filterAdminsArg, { force: true });
    },
    [fetchUsers]
  );

  const {
    page: currentPage,
    setPage: setCurrentPage,
    search: globalFilter,
    setSearch: setGlobalFilter,
    isLoading,
  } = usePaginatedListPage({
    fetchItems: fetchData,
    deps: [selectedOrg, filterAdmins],
  });

  const data = useMemo(
    () => [...users].sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const openTemplateDocModal = (user) => {
    setSelectedUser(user);
    setAddTemplateDoc(true);
  };

  const handleOpenDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleOpenResetPasswordModal = (user) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsResetPasswordModalOpen(false);
    setAddTemplateDoc(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (id) => {
    try {
      await deleteUser(id);
      toast.success('User deleted successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user. Please try again.');
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await setUserPassword(userId, newPassword);
      toast.success('Password reset successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password. Please try again.');
    }
  };

  const clearFilters = () => {
    setGlobalFilter('');
    clearOrgFilter();
    setFilterAdmins(false);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('user_id', { header: 'Cognito ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('organisation_name', {
        header: 'Organisation Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('is_admin', {
        header: 'Is Admin',
        cell: (info) => (info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />),
      }),
      columnHelper.accessor('email', { header: 'Email' }),
      columnHelper.accessor('language', {
        header: 'Language',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('docx_template_file_name', {
        header: 'Template File Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Updated At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => handleOpenEditModal(row.original)}
            onDelete={() => handleOpenDeleteModal(row.original)}
            after={
              <FaKey
                title="Reset Password"
                onClick={() => handleOpenResetPasswordModal(row.original)}
                className="cursor-pointer text-yellow-600"
              />
            }
          >
            <TiDocumentAdd
              size={20}
              title="Template Doc"
              onClick={() => openTemplateDocModal(row.original)}
              className="cursor-pointer text-green-500"
            />
          </RowActions>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const noUsersFound = table.getRowModel().rows.length === 0;

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="User List" onCreate={() => setIsCreateModalOpen(true)} />
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
        <SearchableSelect
          name="org_id"
          value={selectedOrg}
          selectedLabel={selectedOrgLabel}
          onChange={setOrgSelection}
          loadOptions={loadOrgOptions}
          placeholder="Select Organisation"
        />
        <button
          type="button"
          onClick={() => setFilterAdmins(!filterAdmins)}
          className={`flex items-center gap-2 px-5 py-2 border rounded-md shadow-sm bg-white text-gray-500 text-sm cursor-pointer transition-all duration-100 transform
            ${filterAdmins ? 'border-indigo-900 bg-gray-50 scale-105 active:scale-95' : 'hover:bg-gray-50 active:scale-95'}`}
        >
          Select Admins
        </button>
        <ClearFiltersButton show={!!(globalFilter || selectedOrg || filterAdmins)} onClick={clearFilters} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No users found"
        stickyActions
      />
      <ListFooter
        total={totalUsers}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={isLoading}
        isEmpty={noUsersFound}
        itemLabel="users"
      />

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onRequestClose={handleCloseModal}
        onCreate={createUser}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onRequestClose={handleCloseModal}
        user={selectedUser}
        onEdit={editUser}
      />
      {selectedUser && (
        <UploadTemplateDoc
          isOpen={templateDocUpdate}
          onRequestClose={handleCloseModal}
          entity={{ ...selectedUser, type: 'users' }}
        />
      )}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        data={{ ...selectedUser, dataName: 'user' }}
        onDelete={handleDeleteUser}
      />
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onRequestClose={handleCloseModal}
        userId={selectedUser?.user_id}
        setUserPassword={handleResetPassword}
      />
    </div>
  );
};

export default UsersPage;
